// ═══════════════════════════════════════════════════════════════
// Administration — Households Directory
// Family groupings for shelter check-in; vehicle and pet data.
// ═══════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Text, Button, Badge, Stack, Group, TextInput, Textarea,
} from '../../../components/gab/GabComponents';
import { IconPlus, IconSearch, IconHome, IconCar, IconPaw, IconX, IconCheck, IconEdit, IconTrash, IconUsers, IconUserPlus, IconUserMinus } from '@tabler/icons-react';
import { queryRecords, createRecord, updateRecord, deleteRecord } from '../../../api/client';
import { TABLES } from '../../../api/tables';
import { FIELDS } from '../../../api/fields';
import { formatPhone } from '../../../lib/phoneFormat';

type Household = {
  record_id: number;
  householdName: string; primaryContactId: string;
  phone: string; altPhone: string;
  street: string; city: string; state: string; zip: string; county: string;
  vehicleMake: string; vehicleModel: string; vehicleYear: string;
  vehiclePlate: string; vehicleColor: string; vehicleState: string;
  pets: string; specialNeedsNotes: string; notes: string;
};

const fh = FIELDS.households;
const fp = FIELDS.people;

type Person = {
  record_id: number;
  firstName: string; lastName: string;
  phone: string; email: string;
  type: string; status: string;
  householdId: string;
};

function parsePerson(r: Record<string, unknown>): Person {
  return {
    record_id:   r.record_id as number,
    firstName:   (r[fp.firstName]   ?? '') as string,
    lastName:    (r[fp.lastName]    ?? '') as string,
    phone:       (r[fp.phone]       ?? '') as string,
    email:       (r[fp.email]       ?? '') as string,
    type:        (r[fp.type]        ?? '') as string,
    status:      (r[fp.status]      ?? '') as string,
    householdId: (r[fp.householdId] ?? '') as string,
  };
}

const BLANK: Omit<Household, 'record_id'> = {
  householdName: '', primaryContactId: '', phone: '', altPhone: '',
  street: '', city: '', state: '', zip: '', county: '',
  vehicleMake: '', vehicleModel: '', vehicleYear: '',
  vehiclePlate: '', vehicleColor: '', vehicleState: '',
  pets: '', specialNeedsNotes: '', notes: '',
};

function parseHousehold(r: Record<string, unknown>): Household {
  return {
    record_id:      r.record_id as number,
    householdName:  (r[fh.householdName]  ?? '') as string,
    primaryContactId:(r[fh.primaryContactId]??'') as string,
    phone:          (r[fh.phone]          ?? '') as string,
    altPhone:       (r[fh.altPhone]       ?? '') as string,
    street:         (r[fh.street]         ?? '') as string,
    city:           (r[fh.city]           ?? '') as string,
    state:          (r[fh.state]          ?? '') as string,
    zip:            (r[fh.zip]            ?? '') as string,
    county:         (r[fh.county]         ?? '') as string,
    vehicleMake:    (r[fh.vehicleMake]    ?? '') as string,
    vehicleModel:   (r[fh.vehicleModel]   ?? '') as string,
    vehicleYear:    (r[fh.vehicleYear]    ?? '') as string,
    vehiclePlate:   (r[fh.vehiclePlate]   ?? '') as string,
    vehicleColor:   (r[fh.vehicleColor]   ?? '') as string,
    vehicleState:   (r[fh.vehicleState]   ?? '') as string,
    pets:           (r[fh.pets]           ?? '') as string,
    specialNeedsNotes:(r[fh.specialNeedsNotes]??'') as string,
    notes:          (r[fh.notes]          ?? '') as string,
  };
}

function householdToData(form: Omit<Household, 'record_id'>): Record<string, unknown> {
  return {
    [fh.householdName]:   form.householdName,
    [fh.primaryContactId]:form.primaryContactId,
    [fh.phone]:           form.phone,
    [fh.altPhone]:        form.altPhone,
    [fh.street]:          form.street,
    [fh.city]:            form.city,
    [fh.state]:           form.state,
    [fh.zip]:             form.zip,
    [fh.county]:          form.county,
    [fh.vehicleMake]:     form.vehicleMake,
    [fh.vehicleModel]:    form.vehicleModel,
    [fh.vehicleYear]:     form.vehicleYear,
    [fh.vehiclePlate]:    form.vehiclePlate,
    [fh.vehicleColor]:    form.vehicleColor,
    [fh.vehicleState]:    form.vehicleState,
    [fh.pets]:            form.pets,
    [fh.specialNeedsNotes]:form.specialNeedsNotes,
    [fh.notes]:           form.notes,
  };
}

// ── Members Modal ─────────────────────────────────────────────

function MembersModal({ household, allPeople, onUpdate, onClose }: {
  household: Household;
  allPeople: Person[];
  onUpdate: (personId: number, householdId: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch]   = useState('');
  const [working, setWorking] = useState<number | null>(null);
  const [err, setErr]         = useState('');

  const hid = String(household.record_id);
  const linked    = allPeople.filter(p => String(p.householdId) === hid);
  const available = allPeople.filter(p => String(p.householdId) !== hid);
  const filtered  = search
    ? available.filter(p => `${p.firstName} ${p.lastName} ${p.email} ${p.phone}`.toLowerCase().includes(search.toLowerCase()))
    : available;

  const fullName = (p: Person) => [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';

  async function link(p: Person) {
    setWorking(p.record_id); setErr('');
    try {
      await updateRecord(TABLES.people, p.record_id, { [fp.householdId]: hid });
      onUpdate(p.record_id, hid);
    } catch { setErr('Failed to link person'); }
    finally { setWorking(null); }
  }

  async function unlink(p: Person) {
    setWorking(p.record_id); setErr('');
    try {
      await updateRecord(TABLES.people, p.record_id, { [fp.householdId]: '' });
      onUpdate(p.record_id, '');
    } catch { setErr('Failed to remove person'); }
    finally { setWorking(null); }
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px 24px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 4, padding: 28, width: 560, maxWidth: '94vw', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)' }}>
        <Group justify="space-between" mb={6}>
          <div>
            <Text fw={700} size="md">{household.householdName} — Members</Text>
            <Text size="xs" c="dimmed">{linked.length} person{linked.length !== 1 ? 's' : ''} linked to this household</Text>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}><IconX size={18} /></button>
        </Group>

        {err && <Text size="xs" c="var(--color-error)" mb={8}>{err}</Text>}

        {/* Linked members */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
            Linked ({linked.length})
          </div>
          {linked.length === 0 ? (
            <Text size="sm" c="dimmed" style={{ fontStyle: 'italic' }}>No members linked yet — add from the list below</Text>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {linked.map(p => (
                <div key={p.record_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: '#f0f4ff', borderRadius: 4, border: '1px solid #c7d2fe' }}>
                  <IconUsers size={14} color="#4B3FFF" style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1B1C1C' }}>{fullName(p)}</div>
                    {(p.phone || p.email) && <div style={{ fontSize: 12, color: '#7B7D7F' }}>{p.phone ? formatPhone(p.phone) : (p.email || '')}</div>}
                  </div>
                  {p.type && <Badge size="xs" variant="light" color="var(--data-viz-100)">{p.type}</Badge>}
                  {p.status && <Badge size="xs" variant="light" color="#939598">{p.status}</Badge>}
                  <button
                    onClick={() => unlink(p)}
                    disabled={working === p.record_id}
                    title="Remove from household"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#D33423', padding: 2, flexShrink: 0 }}
                  >
                    {working === p.record_id ? '…' : <IconUserMinus size={15} />}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add members */}
        <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          Add from People Directory
        </div>
        <TextInput
          placeholder="Search by name, phone, or email…"
          leftSection={<IconSearch size={14} />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="sm"
          mb="0"
          style={{ marginBottom: 8 }}
        />
        <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {filtered.length === 0 ? (
            <Text size="sm" c="dimmed" style={{ padding: '8px 0', fontStyle: 'italic' }}>
              {search ? 'No people match your search' : available.length === 0 ? 'Everyone is already in a household' : 'Search to find people'}
            </Text>
          ) : filtered.map(p => (
            <div key={p.record_id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 4, border: '1px solid #e2e8f0' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1B1C1C' }}>{fullName(p)}</div>
                {(p.phone || p.email) && <div style={{ fontSize: 12, color: '#7B7D7F' }}>{p.phone ? formatPhone(p.phone) : (p.email || '')}</div>}
              </div>
              {p.type && <Badge size="xs" variant="light" color="#939598">{p.type}</Badge>}
              <button
                onClick={() => link(p)}
                disabled={working === p.record_id}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 26, padding: '0 8px', borderRadius: 4, border: '1px solid #4B3FFF', background: 'white', color: '#4B3FFF', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0, whiteSpace: 'nowrap' }}
              >
                {working === p.record_id ? '…' : <><IconUserPlus size={12} /> Add</>}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Modal ─────────────────────────────────────────────────────

function HouseholdModal({ initial, onSave, onClose }: {
  initial?: Household; onSave: (h: Household) => void; onClose: () => void;
}) {
  const editing = Boolean(initial);
  const [form, setForm] = useState<Omit<Household, 'record_id'>>(initial ? { ...initial } : { ...BLANK });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  function set(k: keyof typeof BLANK, v: string) { setForm(p => ({ ...p, [k]: v })); }

  async function save() {
    if (!form.householdName) { setErr('Household name is required'); return; }
    setSaving(true); setErr('');
    try {
      const data = householdToData(form);
      if (editing && initial) {
        await updateRecord(TABLES.households, initial.record_id, data);
        onSave({ ...form, record_id: initial.record_id });
      } else {
        const result = await createRecord<Record<string, unknown>>(TABLES.households, data);
        onSave({ ...form, record_id: (result.record_id ?? result.id ?? 0) as number });
      }
    } catch (e) { setErr(e instanceof Error ? e.message : 'Save failed'); }
    finally { setSaving(false); }
  }

  const tf = (label: string, k: keyof typeof BLANK, opts?: { type?: string; half?: boolean }) => (
    <div style={{ flex: opts?.half ? '0 0 calc(50% - 6px)' : '1 1 100%' }}>
      <TextInput label={label} value={form[k] as string} onChange={e => set(k, e.target.value)} size="sm" type={opts?.type} mb="0" />
    </div>
  );

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 2000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 20px 24px', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: 4, padding: 28, width: 580, maxWidth: '94vw', boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)', overflowY: 'auto' }}>
        <Group justify="space-between" mb={20}>
          <Text fw={700} size="md">{editing ? 'Edit Household' : 'Add Household'}</Text>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><IconX size={18} /></button>
        </Group>
        <div className="modal-form" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <TextInput label="Household Name *" value={form.householdName} onChange={e => set('householdName', e.target.value)} size="sm" placeholder="e.g. Smith Family" mb="0" />

          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Contact</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {tf('Phone',     'phone',    { type: 'tel', half: true })}
            {tf('Alt Phone', 'altPhone', { type: 'tel', half: true })}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Address</div>
          {tf('Street', 'street')}
          <div style={{ display: 'flex', gap: 8 }}>
            {tf('City',   'city',   { half: true })}
            {tf('State',  'state',  { half: true })}
            {tf('ZIP',    'zip',    { half: true })}
            {tf('County', 'county', { half: true })}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Vehicle</div>
          <div style={{ display: 'flex', gap: 8 }}>
            {tf('Make',  'vehicleMake',  { half: true })}
            {tf('Model', 'vehicleModel', { half: true })}
            {tf('Year',  'vehicleYear',  { half: true })}
            {tf('Color', 'vehicleColor', { half: true })}
            {tf('Plate', 'vehiclePlate', { half: true })}
            {tf('State', 'vehicleState', { half: true })}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pets & Needs</div>
          <Textarea label="Pets (type, count, vaccinated)" value={form.pets} onChange={e => set('pets', e.target.value)} size="sm" rows={2} placeholder='e.g. [{"type":"dog","name":"Rex","vaccinated":true}]' mb="0" />
          <Textarea label="Special Needs Notes" value={form.specialNeedsNotes} onChange={e => set('specialNeedsNotes', e.target.value)} size="sm" rows={2} mb="0" />
          <Textarea label="Notes" value={form.notes} onChange={e => set('notes', e.target.value)} size="sm" rows={2} mb="0" />

          {err && <Text size="xs" c="var(--color-error)">{err}</Text>}
          <Group justify="flex-end" gap={8}>
            <Button variant="light" color="#939598" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button size="sm" onClick={save} loading={saving} leftSection={<IconCheck size={13} />}>
              {editing ? 'Save Changes' : 'Create Household'}
            </Button>
          </Group>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ── Page ──────────────────────────────────────────────────────

export default function HouseholdsPage() {
  const [households,  setHouseholds]  = useState<Household[]>([]);
  const [people,      setPeople]      = useState<Person[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [modal,       setModal]       = useState<{ open: boolean; editing?: Household }>({ open: false });
  const [membersFor,  setMembersFor]  = useState<Household | null>(null);

  useEffect(() => {
    if (!TABLES.households) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      queryRecords<Record<string, unknown>>(TABLES.households, { limit: 500, sort: { field: fh.householdName, direction: 'asc' } }),
      queryRecords<Record<string, unknown>>(TABLES.people,     { limit: 500 }),
    ])
      .then(([hRes, pRes]) => {
        setHouseholds(hRes.records.map(parseHousehold));
        setPeople(pRes.records.map(parsePerson));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = households.filter(h =>
    !search || h.householdName.toLowerCase().includes(search.toLowerCase())
  );

  function memberCount(h: Household) {
    return people.filter(p => String(p.householdId) === String(h.record_id)).length;
  }

  async function remove(h: Household) {
    if (!confirm(`Delete household "${h.householdName}"? This cannot be undone.`)) return;
    await deleteRecord(TABLES.households, h.record_id);
    setHouseholds(hs => hs.filter(x => x.record_id !== h.record_id));
  }

  if (!TABLES.households) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <IconHome size={40} style={{ marginBottom: 16, opacity: 0.2 }} />
        <Text size="sm" c="dimmed">Households table not yet provisioned. Run <code>node scripts/provision-directory.mjs</code>.</Text>
      </div>
    );
  }

  return (
    <Stack gap={12}>
      <Group justify="space-between" align="center">
        <div>
          <Text fw={700} size="xl">Households</Text>
          <Text size="sm" c="dimmed">{households.length} household{households.length !== 1 ? 's' : ''} on file</Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} size="sm" onClick={() => setModal({ open: true })}>
          Add Household
        </Button>
      </Group>

      <TextInput
        placeholder="Search households..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ maxWidth: 320 }}
      />

      {loading ? (
        <Text size="sm" c="dimmed">Loading…</Text>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#bbb' }}>
          <IconHome size={40} style={{ marginBottom: 8, opacity: 0.2 }} />
          <Text size="sm">{search ? 'No households match your search' : 'No households on file yet'}</Text>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 8 }}>
          {filtered.map(h => (
            <div key={h.record_id} style={{ border: '1px solid #e2e4e8', borderRadius: 4, padding: 16, background: '#fff', display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Group justify="space-between">
                <Group gap={8}>
                  <IconHome size={16} color="#6366f1" />
                  <Text fw={600} size="sm">{h.householdName || 'Unnamed Household'}</Text>
                </Group>
                <Group gap={4}>
                  <button onClick={() => setMembersFor(h)} title="Manage members" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4B3FFF', padding: 2 }}><IconUsers size={14} /></button>
                  <button onClick={() => setModal({ open: true, editing: h })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#666', padding: 2 }}><IconEdit size={14} /></button>
                  <button onClick={() => remove(h)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d33423', padding: 2 }}><IconTrash size={14} /></button>
                </Group>
              </Group>

              {h.phone && <Text size="xs" c="dimmed">{h.phone}</Text>}
              {(h.city || h.state) && <Text size="xs" c="dimmed">{[h.city, h.state].filter(Boolean).join(', ')}</Text>}

              <Group gap={6}>
                <Badge
                  size="xs"
                  leftSection={<IconUsers size={10} />}
                  variant="light"
                  color="var(--data-viz-100)"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setMembersFor(h)}
                >
                  {memberCount(h)} member{memberCount(h) !== 1 ? 's' : ''}
                </Badge>
                {(h.vehicleMake || h.vehiclePlate) && (
                  <Badge size="xs" leftSection={<IconCar size={10} />} variant="light" color="var(--color-primary-700)">
                    {[h.vehicleMake, h.vehicleModel].filter(Boolean).join(' ') || h.vehiclePlate}
                  </Badge>
                )}
                {h.pets && (
                  <Badge size="xs" leftSection={<IconPaw size={10} />} variant="light" color="var(--color-success)">Pets on file</Badge>
                )}
                {h.specialNeedsNotes && (
                  <Badge size="xs" variant="light" color="#F86A0B">Special needs</Badge>
                )}
              </Group>
            </div>
          ))}
        </div>
      )}

      {modal.open && (
        <HouseholdModal
          initial={modal.editing}
          onSave={(h) => {
            if (modal.editing) {
              setHouseholds(hs => hs.map(x => x.record_id === h.record_id ? h : x));
            } else {
              setHouseholds(hs => [h, ...hs]);
            }
            setModal({ open: false });
          }}
          onClose={() => setModal({ open: false })}
        />
      )}

      {membersFor && (
        <MembersModal
          household={membersFor}
          allPeople={people}
          onUpdate={(personId, householdId) => {
            setPeople(prev => prev.map(p => p.record_id === personId ? { ...p, householdId } : p));
          }}
          onClose={() => setMembersFor(null)}
        />
      )}
    </Stack>
  );
}
